/**
 * CountrySelect Component
 *
 * Searchable country selector.
 *
 * The COUNTRIES are the caller's: `useCountries().countries` from
 * @tonnie/locale — named in the reader's language and sorted as that language
 * sorts. This used to read `COUNTRIES` from @tonnie/foundation, whose `name` is
 * English, so every picker said "Brazil" to a pt-PT reader (F34). Like every
 * other text this package renders, the names are passed in translated.
 * Flags are `<CountryFlag>` images — an emoji flag is two letters on Windows (F32).
 */

import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';

import { cn } from '../lib/utils';
import { CountryFlag } from './country-flag';
import { Button } from '../primitives/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../primitives/command';
import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover';

/** One selectable country, already named in the reader's language. */
export interface CountryOption {
  /** ISO 3166-1 alpha-2 */
  code: string;
  /** Translated name */
  name: string;
  /** International dialling code, e.g. "+351" */
  phoneCode: string;
}

export interface CountrySelectProps {
  /** Every selectable country, translated and sorted — `useCountries().countries`. */
  countries: readonly CountryOption[];
  /** Selected country code (e.g., "US", "PT") */
  value?: string;
  /** Callback when country is selected */
  onValueChange: (value: string) => void;
  /** Placeholder text when no country selected. Pass a translated string. */
  placeholder?: string;
  /** Placeholder text for search input. Pass a translated string. */
  searchPlaceholder?: string;
  /** Message shown when no country matches search. Pass a translated string. */
  emptyMessage?: string;
  /** Disable the selector */
  disabled?: boolean;
  /** Country codes to exclude from list */
  excludeCodes?: string[];
  /** Additional CSS classes */
  className?: string;
  /** Id of the trigger — what a form's `<Label htmlFor>` points at. */
  id?: string;
  /** Marks the trigger invalid for assistive technology. */
  'aria-invalid'?: boolean;
  /** The trigger's error message, when there is one. */
  'aria-describedby'?: string;
  'data-testid'?: string;
}

export function CountrySelect({
  countries,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled = false,
  excludeCodes = [],
  className,
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'data-testid': testId,
}: CountrySelectProps) {
  const [open, setOpen] = useState(false);

  const selectedCountry = value ? countries.find((c) => c.code === value.toUpperCase()) : undefined;
  const filteredCountries = countries.filter((country) => !excludeCodes.includes(country.code));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid || undefined}
          aria-describedby={ariaDescribedBy}
          data-testid={testId}
          disabled={disabled}
          className={cn('w-full justify-between h-10', className)}
        >
          {selectedCountry ? (
            <div className="flex items-center gap-2">
              <CountryFlag code={selectedCountry.code} size="sm" />
              <span className="text-xs text-muted-foreground">{selectedCountry.code}</span>
              <span className="flex-1 text-left">{selectedCountry.name}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {filteredCountries.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.code}`}
                  onSelect={() => {
                    onValueChange(country.code);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === country.code ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <CountryFlag code={country.code} size="sm" className="mr-2" />
                  <span className="text-xs text-muted-foreground mr-2">{country.code}</span>
                  <span>{country.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
